import React, { useCallback, useEffect, useState } from 'react';

import { Button, Checkbox, message, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import { selectUserSettings, updateUserFilterSettings } from '../../../store/reducers/sessionReducer';
// Import Redux actions/selectors
import {
    defaultFilters,
    fetchServices,
    resetFilters,
    selectActiveTabKey,
    selectCalendarFilters,
    selectFiltersForActiveTab,
    selectIsFilterPopupVisible,
    selectServiceInfos,
    selectSeveritiesForFilter,
    selectStatusInfo,
    selectSuppliersForFilter,
    selectVehiclesForFilter,
    setPopupVisibility,
    updateFilters,
    defaultInitialTabs,
    updateCalendarResources
} from '../../../store/serviceCalendarSlice';

export const ServiceCalendarPopupFilter = (props) => {
    const { type } = props; // 'daily' or 'calendar'
    const dispatch = useDispatch();
    const isVisible = useSelector(selectIsFilterPopupVisible);

    // --- Get Context-Specific State ---
    const activeTabKey = useSelector(selectActiveTabKey);
    const userSettings = useSelector(selectUserSettings);

    // Select the correct filters based on the type prop
    const currentFilters = useSelector(type === 'daily' ? selectFiltersForActiveTab : selectCalendarFilters);

    const statusOptions = useSelector(selectStatusInfo);
    const serviceTypeOptions = useSelector(selectServiceInfos);
    const vehicleOptions = useSelector(selectVehiclesForFilter);
    const severityOptions = useSelector(selectSeveritiesForFilter);
    const supplierOptions = useSelector(selectSuppliersForFilter);
    // ---

    const [loading, setLoading] = useState(false);
    const [tabLabel, setTabLabel] = useState('');

    useEffect(() => {
        if (type === 'daily' && activeTabKey) {
            let currentTabLabel = '';
            if (userSettings?.tabs?.length > 0) {
                currentTabLabel = userSettings?.tabs?.find((tab) => tab.key === activeTabKey).label;
            } else {
                // Fallback to default tab label if userSettings is not available or empty
                currentTabLabel = defaultInitialTabs.find((tab) => tab.key === activeTabKey).label;
            }
            setTabLabel(currentTabLabel || '');
        }
    }, [userSettings, activeTabKey, type]);

    const handleFilterChange = useCallback(
        (filterUpdate) => {
            const payload = { filterUpdate };
            if (type === 'daily') {
                if (!activeTabKey) return;
                payload.tabKey = activeTabKey;
            }
            dispatch(updateFilters(payload));
        },
        [dispatch, type, activeTabKey]
    );

    const handleMultiSelectChange = useCallback(
        (key, value) => {
            const currentSelection = currentFilters[key] || [];
            const newSelection = currentSelection.includes(value) ? currentSelection.filter((item) => item !== value) : [...currentSelection, value];
            handleFilterChange({ [key]: newSelection });
        },
        [currentFilters, handleFilterChange]
    ); // Depends on currentFilters which is context-aware
    // --- End Handlers ---

    // Handle closing the popup (remains the same)
    const handleClose = useCallback(() => {
        dispatch(setPopupVisibility({ popupName: 'filter', isVisible: false }));
    }, [dispatch]);

    // Handle resetting filters (Context-Aware)
    const handleReset = useCallback(async () => {
        if (type === 'daily' && !activeTabKey) return; // Guard for daily type

        try {
            setLoading(true);
            const resetPayload = {};
            let settingsUpdateKey = '';
            let updatedSettingsValue = {};

            if (type === 'daily') {
                resetPayload.tabKey = activeTabKey;
                settingsUpdateKey = 'daily_service_filters';
                const currentSavedFilters = userSettings?.daily_service_filters || {};
                updatedSettingsValue = {
                    ...currentSavedFilters,
                    [activeTabKey]: {
                        ...currentSavedFilters[activeTabKey],
                        ...defaultFilters
                    }
                };
            } else {
                // type === 'calendar'
                settingsUpdateKey = 'service_calendar_filters';
                updatedSettingsValue = { ...defaultFilters }; // Reset calendar filters to defaults
            }

            // 1. Reset filters in Redux state
            dispatch(resetFilters(resetPayload));

            // 2. Reset calendar resources to show all vehicles
            if (type === 'calendar') {
                dispatch(updateCalendarResources({ selectedVehicleIds: null }));
            }

            // 3. Persist the reset filters in user settings
            await dispatch(updateUserFilterSettings({ [settingsUpdateKey]: updatedSettingsValue })).unwrap();

            message.success(`Filters cleared for ${type === 'daily' ? 'this tab' : 'calendar'}`);

            dispatch(fetchServices());

            handleClose(); // Close popup after reset
        } catch (error) {
            message.error('Failed to clear filters');
            console.error('Clear error:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, type, activeTabKey, userSettings, handleClose]);

    // Handle selecting all options (Context-Aware)
    const handleSelectAll = useCallback(() => {
        if (type === 'daily' && !activeTabKey) return;

        const allFilters = {
            selectedStatutory: true,
            selectedRoadWorthy: true,
            selectedOffRoad: true,
            selectedScheduled: true,
            selectedUnscheduled: true,
            selectedDefect: true,
            selectedSeverity: 'Choose',
            selectedSupplier: '0',
            selectedStatuses: statusOptions.map((status) => status.id),
            selectedServiceTypes: serviceTypeOptions.map((service) => service.id),
            selectedVehicles: vehicleOptions.map((vehicle) => vehicle.vehicleId)
        };
        const payload = { filterUpdate: allFilters };
        if (type === 'daily') {
            payload.tabKey = activeTabKey;
        }
        dispatch(updateFilters(payload));
    }, [dispatch, type, activeTabKey, statusOptions, serviceTypeOptions, vehicleOptions]);

    // Handle applying and SAVING the filters (Context-Aware)
    const handleApplyAndSaveFilters = useCallback(async () => {
        if (type === 'daily' && !activeTabKey) return;

        try {
            setLoading(true);
            let settingsUpdateKey = '';
            let updatedSettingsValue = {};

            if (type === 'daily') {
                settingsUpdateKey = 'daily_service_filters';
                const currentSavedFilters = userSettings?.daily_service_filters || {};
                updatedSettingsValue = {
                    ...currentSavedFilters,
                    [activeTabKey]: {
                        ...currentSavedFilters[activeTabKey], // Keep other tab settings (column order, etc.)
                        ...currentFilters // Override with current filter values
                    }
                };
            } else {
                // type === 'calendar'
                settingsUpdateKey = 'service_calendar_filters';
                updatedSettingsValue = currentFilters;
            }

            await dispatch(updateUserFilterSettings({ [settingsUpdateKey]: updatedSettingsValue })).unwrap();

            // Apply vehicle filter locally to calendar resources if vehicles are selected
            if (
                currentFilters.selectedVehicles &&
                currentFilters.selectedVehicles.length > 0 &&
                currentFilters.selectedVehicles.length < vehicleOptions.length
            ) {
                // Dispatch action to filter calendar resources based on selected vehicles
                dispatch(updateCalendarResources({ selectedVehicleIds: currentFilters.selectedVehicles }));
            } else {
                // Show all vehicles if none selected or all selected
                dispatch(updateCalendarResources({ selectedVehicleIds: null }));
            }

            dispatch(fetchServices());

            message.success(`Filters applied and saved for ${type === 'daily' ? 'this tab' : 'calendar'}`);
            handleClose(); // Close the popup
        } catch (error) {
            message.error('Failed to apply or save filters');
            console.error('Failed to apply/save filters:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, type, activeTabKey, currentFilters, userSettings, vehicleOptions.length, handleClose]);

    if (!isVisible || (type === 'daily' && !activeTabKey)) {
        return null;
    }

    return (
        <div className="search-filter-modal">
            <div className="modal-header"></div>
            <div className="search-filter-modal-body">
                <div className="modal-title">Search Filter {type === 'daily' ? `(Tab: ${tabLabel})` : '(Calendar)'}</div>
                <div className="search-filter-content">
                    {/* Column 1: Boolean Toggles & Selects */}
                    <div className="col">
                        {/* Checkboxes using currentFilters and handleFilterChange */}
                        <div className="custom-checkbox label-before mt-6 mb-24">
                            <input
                                id="filter-statutory"
                                type="checkbox"
                                checked={currentFilters.selectedStatutory}
                                onChange={(e) => handleFilterChange({ selectedStatutory: e.target.checked })}
                            />
                            <label htmlFor="filter-statutory">Statutory</label>
                        </div>
                        <div className="custom-checkbox label-before">
                            <input
                                id="filter-roadworthy"
                                type="checkbox"
                                checked={currentFilters.selectedRoadWorthy}
                                onChange={(e) => handleFilterChange({ selectedRoadWorthy: e.target.checked })}
                            />
                            <label htmlFor="filter-roadworthy">Road Worthy</label>
                        </div>
                        <div className="custom-checkbox label-before mb-24">
                            <input
                                id="filter-offroad"
                                type="checkbox"
                                checked={currentFilters.selectedOffRoad}
                                onChange={(e) => handleFilterChange({ selectedOffRoad: e.target.checked })}
                            />
                            <label htmlFor="filter-offroad">Off Road</label>
                        </div>
                        <div className="custom-checkbox label-before">
                            <input
                                id="filter-scheduled"
                                type="checkbox"
                                checked={currentFilters.selectedScheduled}
                                onChange={(e) => handleFilterChange({ selectedScheduled: e.target.checked })}
                            />
                            <label htmlFor="filter-scheduled">Scheduled</label>
                        </div>
                        <div className="custom-checkbox label-before">
                            <input
                                id="filter-unscheduled"
                                type="checkbox"
                                checked={currentFilters.selectedUnscheduled}
                                onChange={(e) => handleFilterChange({ selectedUnscheduled: e.target.checked })}
                            />
                            <label htmlFor="filter-unscheduled">Unscheduled</label>
                        </div>
                        <div className="custom-checkbox label-before">
                            <input
                                id="filter-defect"
                                type="checkbox"
                                checked={currentFilters.selectedDefect}
                                onChange={(e) => handleFilterChange({ selectedDefect: e.target.checked })}
                            />
                            <label htmlFor="filter-defect">Defects</label>
                        </div>

                        {type === 'daily' && (
                            <div className="custom-checkbox label-before">
                                <input
                                    id="filter-nostartdate"
                                    type="checkbox"
                                    checked={currentFilters.selectedNoStartDate}
                                    onChange={(e) => handleFilterChange({ selectedNoStartDate: e.target.checked })}
                                />
                                <label htmlFor="filter-nostartdate">No Start Date</label>
                            </div>
                        )}

                        {/* Selects using currentFilters and handleFilterChange */}
                        <Select
                            style={{ width: '100%' }}
                            value={currentFilters.selectedSeverity || 'Choose'}
                            onChange={(value) => handleFilterChange({ selectedSeverity: value })}
                            className="mb-12 mt-26"
                            options={severityOptions.map((severity) => ({
                                value: severity.severityType,
                                label: severity.severityType === 'Choose' ? 'Choose Severity' : severity.severityType
                            }))}
                        />
                        {/* <Select
                            style={{ width: '100%' }}
                            value={currentFilters.selectedSupplier || '0'}
                            onChange={(value) => handleFilterChange({ selectedSupplier: value })}
                            className="mb-20"
                            options={supplierOptions.map((supplier) => ({
                                value: supplier.supplierId,
                                label: supplier.supplierName // Already includes "All Suppliers"
                            }))}
                        /> */}
                    </div>
                    {/* Column 2: Status Checkboxes */}
                    <div className="col">
                        <div className="flex mb-2 gap-1 ml-[5px]">
                            <Checkbox
                                id="select-all-status"
                                checked={statusOptions.length > 0 && currentFilters.selectedStatuses?.length === statusOptions.length}
                                onChange={(e) => {
                                    const allStatusIds = statusOptions.map((status) => status.id);
                                    handleFilterChange({ selectedStatuses: e.target.checked ? allStatusIds : [] });
                                }}
                            />
                            <div className="text-md font-semibold">Status</div>
                        </div>
                        <div className="checkbox-outer outline-checkboxes">
                            {statusOptions.length > 0 ? (
                                statusOptions.map((status, index) => (
                                    <div key={`status-${status.id}`} className="custom-checkbox">
                                        <input
                                            type="checkbox"
                                            id={`statusType-${index}`}
                                            checked={currentFilters.selectedStatuses.includes(status.id)}
                                            onChange={() => handleMultiSelectChange('selectedStatuses', status.id)}
                                            className="mr-2"
                                        />
                                        <label
                                            htmlFor={`statusType-${index}`}
                                            className="text-sm"
                                            style={{
                                                backgroundColor: status.backgroundColor,
                                                color: status.textColor,
                                                border: `1px solid ${status.borderColor}`
                                            }}>
                                            {status.type}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p>Loading status...</p>
                            )}
                        </div>
                    </div>
                    {/* Column 3: Service Type Checkboxes */}
                    <div className="col">
                        <div className="flex mb-2 gap-1 ml-[5px]">
                            <Checkbox
                                id="select-all-service-types"
                                checked={serviceTypeOptions.length > 0 && currentFilters.selectedServiceTypes?.length === serviceTypeOptions.length}
                                onChange={(e) => {
                                    const allServiceTypeIds = serviceTypeOptions.map((service) => service.id);
                                    handleFilterChange({ selectedServiceTypes: e.target.checked ? allServiceTypeIds : [] });
                                }}
                            />
                            <div className="text-md font-semibold">Service Types</div>
                        </div>
                        <div className="checkbox-outer filled-checkboxes">
                            {serviceTypeOptions.length > 0 ? (
                                serviceTypeOptions.map((service, index) => (
                                    <div key={`service-${service.id}`} className="custom-checkbox">
                                        <input
                                            type="checkbox"
                                            id={`serviceType-${index}`}
                                            checked={currentFilters.selectedServiceTypes.includes(service.id)}
                                            onChange={() => handleMultiSelectChange('selectedServiceTypes', service.id)}
                                            className="mr-2"
                                        />
                                        <label
                                            htmlFor={`serviceType-${index}`}
                                            className="text-sm"
                                            style={{
                                                backgroundColor: service.backgroundColor,
                                                color: service.textColor,
                                                border: `1px solid ${service.borderColor}`
                                            }}>
                                            {service.type}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p>Loading service types...</p>
                            )}
                        </div>
                    </div>
                    {/* Column 4: Vehicle Checkboxes */}
                    <div className="col">
                        <div className="flex mb-2 gap-1 ml-[5px]">
                            <Checkbox
                                id="select-all-vehicles"
                                checked={vehicleOptions.length > 0 && currentFilters.selectedVehicles?.length === vehicleOptions.length}
                                onChange={(e) => {
                                    const allVehicleIds = vehicleOptions.map((vehicle) => vehicle.vehicleId);
                                    handleFilterChange({ selectedVehicles: e.target.checked ? allVehicleIds : [] });
                                }}
                            />
                            <div className="text-md font-semibold">Vehicles</div>
                        </div>
                        <div className="checkbox-outer default-checkboxes">
                            {vehicleOptions.length > 0 ? (
                                vehicleOptions.map((vehicle, index) => (
                                    <div key={`vehicle-${vehicle.vehicleId}`} className="custom-checkbox">
                                        <input
                                            type="checkbox"
                                            id={`vehicle-${index}`}
                                            checked={currentFilters.selectedVehicles.includes(vehicle.vehicleId)}
                                            onChange={() => handleMultiSelectChange('selectedVehicles', vehicle.vehicleId)}
                                            className="mr-2"
                                        />
                                        <label htmlFor={`vehicle-${index}`} className="text-sm">
                                            {`${vehicle.vehicleReg || 'N/A'} ${vehicle.carName || ''}`}
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p>Loading vehicles...</p>
                            )}
                        </div>
                    </div>
                </div>{' '}
                {/* End search-filter-content */}
                {/* Action Buttons */}
                <div className="action-btn justify-between">
                    <div>
                        <Button type="secondary" loading={loading} className="btn-filter-in-popup primary-btn" onClick={handleApplyAndSaveFilters}>
                            Apply & Save Filters {/* Changed text */}
                        </Button>
                        <button className="btn-filter-in-popup primary-btn ms-10" onClick={handleSelectAll}>
                            Select All
                        </button>
                    </div>
                    <div>
                        <button className="btn-filter-in-popup grey-btn ms-10" onClick={handleReset}>
                            Clear Filters
                        </button>
                        <button className="btn-filter-in-popup grey-btn ms-10" onClick={handleClose}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
